import { useState } from "react";
import { Link } from "react-router-dom";
import { artists } from "@/mocks/portfolio";
import FrameOverlay from "@/components/base/FrameOverlay";
import { useInView } from "@/hooks/useInView";

export default function WorksIndex() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section className="bg-background-50 py-24 md:py-36 border-t border-foreground-950/10" id="artists">
      <div className="px-6 md:px-12">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24 max-w-7xl mx-auto">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-foreground-700 mb-5">
              ( Artists ) — Featured Masters
            </p>
            <h2 className="font-display font-black text-foreground-950 leading-[0.88] tracking-[-0.04em] text-[12vw] md:text-[7vw] lg:text-[5.5vw]">
              The <em className="font-serif italic font-light">collective</em>,
              <br />
              four <em className="font-serif italic font-light">visions</em>.
            </h2>
          </div>
          <div className="max-w-sm">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed font-serif">
              Four master artists, each devoted to a distinct classical tradition. Together, we form a sanctuary where ancient craft meets living canvas.
            </p>
          </div>
        </div>

        {/* Artists list */}
        <div className="max-w-7xl mx-auto border-t border-foreground-950/15">
          {artists.map((p, i) => (
            <ArtistRow
              key={p.index}
              artist={p}
              index={i}
              hovered={hovered}
              onHover={setHovered}
            />
          ))}
        </div>

      </div>
    </section>
  );
}

function ArtistRow({
  artist: p,
  index: i,
  hovered,
  onHover,
}: {
  artist: typeof artists[number];
  index: number;
  hovered: number | null;
  onHover: (v: number | null) => void;
}) {
  const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <Link
      ref={ref}
      to={p.to}
      onMouseEnter={() => onHover(i)}
      onMouseLeave={() => onHover(null)}
      className={`group relative block border-b border-foreground-950/15 cursor-pointer transition-all duration-700 ease-out ${
        isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${i * 100}ms` }}
    >
      <div className="grid grid-cols-12 gap-4 md:gap-6 py-8 md:py-12 items-center transition-colors duration-500">
        {/* Index number */}
        <div className="col-span-2 md:col-span-1">
          <span className="font-display text-xs md:text-sm uppercase tracking-[0.22em] text-foreground-700">
            ( {p.index} )
          </span>
        </div>

        {/* Title */}
        <div className="col-span-10 md:col-span-5">
          <h3 className="font-display font-black text-foreground-950 leading-[0.92] tracking-[-0.03em] text-4xl md:text-5xl lg:text-6xl transition-transform duration-500 group-hover:translate-x-2">
            {p.name}
            <em className="font-serif italic font-light"> {p.italic}</em>
          </h3>
          <p className="font-display text-[11px] uppercase tracking-[0.22em] text-foreground-700 mt-3">
            {p.specialty}
          </p>
        </div>

        {/* Tags / years */}
        <div className="col-span-12 md:col-span-3 flex flex-wrap items-center gap-2">
          {p.tags.map((t) => (
            <span
              key={t}
              className="px-3 py-1.5 rounded-full border border-foreground-950/25 text-foreground-950/80 text-[10px] uppercase tracking-[0.16em] font-display whitespace-nowrap"
            >
              {t}
            </span>
          ))}
          <span className="font-display text-[11px] uppercase tracking-[0.22em] text-foreground-700 ml-1">
            {p.years}
          </span>
        </div>

        {/* Arrow */}
        <div className="col-span-12 md:col-span-3 flex md:justify-end items-center gap-3">
          <span className="font-display text-[11px] uppercase tracking-[0.22em] text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            View Profile
          </span>
          <span className="w-12 h-12 md:w-14 md:h-14 rounded-full border border-foreground-950/30 flex items-center justify-center text-foreground-950 transition-all duration-500 group-hover:bg-primary-500 group-hover:text-background-50 group-hover:border-primary-500">
            <i className="ri-arrow-right-up-line text-xl md:text-2xl"></i>
          </span>
        </div>
      </div>

      {/* Hover preview image */}
      <div
        className={`pointer-events-none absolute right-32 md:right-24 lg:right-28 top-3 md:top-4 lg:top-5 w-44 md:w-56 lg:w-64 aspect-[3/4] overflow-hidden transition-all duration-500 ease-out z-10 ${
          hovered === i
            ? "opacity-100 translate-y-0 scale-100 rotate-[-2deg]"
            : "opacity-0 -translate-y-4 scale-90 rotate-0"
        }`}
        style={{ transitionProperty: "opacity, transform" }}
      >
        <div className="relative w-full h-full">
          <img
            src={p.image}
            alt={`${p.name} ${p.italic}`}
            className="w-full h-full object-cover object-top"
          />
          <FrameOverlay />
        </div>
        <div className="absolute inset-0 ring-1 ring-accent-500/20 pointer-events-none" />
      </div>

      {/* Description (revealed on hover) */}
      <div
        className={`overflow-hidden transition-all duration-500 ${
          hovered === i ? "max-h-32 opacity-100 pb-8 md:pb-10" : "max-h-0 opacity-0"
        }`}
      >
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-12 md:col-start-2 md:col-span-7">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed font-serif">
              {p.description}
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
}